import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  Clock,
  MapPin,
  Home,
  Building2,
  Building,
  BookOpen,
  Shirt,
  PackagePlus,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Achères et dans le 78 ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison locale
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de cartons {city.aCity} et dans le 78
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous savons que le temps est précieux lors d&apos;un
              déménagement. C&apos;est pourquoi nous assurons un service
              de livraison rapide et flexible, calé sur vos
              disponibilités.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Secteurs desservis",
                body: "Nous livrons partout à Achères (Centre-ville, Les Plantes, Barbière, etc.) ainsi que dans les communes limitrophes (Conflans-Sainte-Honorine, Poissy, Andrésy, Saint-Germain-en-Laye).",
                Icon: MapPin,
              },
              {
                title: "Rapidité",
                body: "Commandez vos cartons en ligne et planifiez votre livraison selon vos disponibilités.",
                Icon: Clock,
              },
              {
                title: "Sécurité",
                body: "Vos produits arrivent propres, secs et prêts à être dépliés.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Packs sur-mesure ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Packs sur-mesure
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos formules &amp; packs cartons déménagement sur-mesure
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous faciliter la vie, nous avons conçu des packs
            complets adaptés à la taille de votre logement {city.aCity}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Pack Studio / T1",
              body: "Idéal pour les petits volumes. Contient des cartons standards, des cartons pour livres, du ruban adhésif et du film bulle.",
              Icon: Home,
            },
            {
              title: "Pack Appartement (T2/T3)",
              body: "Le compromis parfait pour les couples ou les jeunes familles s'installant dans le 78.",
              Icon: Building,
            },
            {
              title: "Pack Maison (T4 et plus)",
              body: "Un volume généreux de cartons renforcés, de penderies pour vos vêtements sur cintres, et de protections pour vos objets fragiles.",
              Icon: Building2,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-acheres-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous nos packs
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-acheres-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; protections
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Produits à l'unité ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Catalogue à l&apos;unité
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos produits disponibles à l&apos;unité
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour les ajustements de dernière minute ou les besoins
            spécifiques, retrouvez l&apos;intégralité de notre catalogue
            disponible à l&apos;unité — livré {city.aCity}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Cartons standards et renforcés",
              body: "Idéaux pour la vaisselle et le linge. Double cannelure pour les objets lourds et fragiles.",
              Icon: Boxes,
            },
            {
              title: "Cartons spécial livres",
              body: "Plus petits pour éviter qu'ils ne soient trop lourds. Format optimisé pour soulager les bras lors du chargement.",
              Icon: BookOpen,
            },
            {
              title: "Cartons penderie",
              body: "Avec tringle intégrée pour déménager vos vêtements sans les froisser, ni les décrocher de leurs cintres.",
              Icon: Shirt,
            },
            {
              title: "Fournitures de protection",
              body: "Rouleaux de papier bulle, film étirable, adhésifs silencieux et calage pour sécuriser tous vos objets fragiles.",
              Icon: PackagePlus,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
                <div className="mt-3 flex items-center gap-2 text-[12.5px] font-semibold text-[#237cb5]">
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
                  Disponible à l&apos;unité
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-acheres-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-acheres-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-acheres-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
